import { useTranslation } from 'next-i18next';

import { useCallback, useState } from 'react';

import { useToken } from '@chakra-ui/react';

import {
    mapStateSlice,
    setIsMapLoadedAction,
    setLocationDetailsAction,
    setSelectedAccommodationAction,
    useAppDispatch,
    useAppSelector,
} from '@app/app-state';
import { LocationType, LocationTypeEnum } from '@app/enums';
import { toNumericIds } from '@app/helpers/common.helpers';
import { mapRatingToApi } from '@app/helpers/map-api-params.helpers';
import { calculateZoomFromRadius } from '@app/helpers/map-utils';
import { applyPropertyTypeExpansion } from '@app/helpers/property-type-expansion.helpers';
import { AccommodationItemModel, LocationDetailsModel } from '@app/models';
import {
    fetchMapAccommodations,
    getGeoDetails,
    getLocationDetails,
} from '@app/state/api-thunks/map-accommodations.thunk';

import { useSearch } from './useSearch';

/**
 * Hook to access and update map search state
 */
export function useMapSearch() {
    const dispatch = useAppDispatch();
    const {
        searchCriteria,
        filters,
        hasGeoChanged,
        isFiltersContentVisible,
        updateSearchCriteria,
        updateSearchFilters,
        toggleFiltersVisibility,
        setFiltersVisibility,
        getAsUrlParams,
        isMobileRes,
        ...rest
    } = useSearch();
    const { t } = useTranslation('common', { keyPrefix: 'map' });
    const mapAccommodations = useAppSelector(mapStateSlice.selectors.selectMapAccommodations);
    const realtimeBookingModelAccommodationsCount = useAppSelector(
        mapStateSlice.selectors.selectMapRealtimeBookingModelAccommodationsCount,
    );
    const isMapLoaded = useAppSelector(mapStateSlice.selectors.selectIsMapLoaded);
    const locationDetails = useAppSelector(mapStateSlice.selectors.selectLocationDetails);
    const isLoadingAccommodations = useAppSelector(
        mapStateSlice.selectors.selectIsLoadingAccommodations,
    );
    const accommodationsError = useAppSelector(mapStateSlice.selectors.selectAccommodationsError);
    const selectedAccommodation = useAppSelector(
        mapStateSlice.selectors.selectSelectedAccommodation,
    );
    const [mapSidebarWidth] = useToken('sizes', ['mapSidebarWidth']);

    const [isClustering, setIsClustering] = useState(false);

    const setSelectedAccommodation = useCallback((accommodation: AccommodationItemModel | null) => {
        dispatch(setSelectedAccommodationAction({ accommodation }));
    }, []);

    const setLocationDetails = useCallback((data: LocationDetailsModel) => {
        dispatch(
            setLocationDetailsAction({
                locationDetails: data,
            }),
        );
    }, []);

    const setMapPageTitle = useCallback(
        async (geoId?: string, locationType?: LocationType): Promise<void> => {
            const disallowedTypes: LocationType[] = [
                LocationTypeEnum.Accommodation,
                LocationTypeEnum.POI,
            ];

            let locative;
            if (geoId && locationType && !disallowedTypes.includes(locationType)) {
                const geoDetails = await getGeoDetails(geoId, locationType);
                locative = geoDetails?.varietyLocative || geoDetails?.name;
            } else {
                locative = t('metadata.defaultGeoLocative');
            }

            document.title = t('metadata.title', {
                value: locative,
            });
        },
        [t],
    );

    const setMapLocationDetails = useCallback(
        async (geoId: string, locationType: LocationType): Promise<void> => {
            const locationDetails = await getLocationDetails(geoId, locationType);

            if (!locationDetails) {
                return;
            }

            const {
                radius,
                accommodationId,
                location: { lat },
            } = locationDetails.details;

            const innerWidth = isMobileRes
                ? window.innerWidth
                : window.innerWidth - parseInt(mapSidebarWidth, 10);

            const calculatedRadius =
                accommodationId && isMobileRes ? Math.round(radius / 2) : radius;
            const zoom = calculateZoomFromRadius(
                calculatedRadius,
                innerWidth,
                window.innerHeight,
                lat,
            );
            setLocationDetails({
                ...locationDetails,
                details: {
                    ...locationDetails.details,
                    zoom,
                },
            });
        },
        [setLocationDetails, isMobileRes, mapSidebarWidth],
    );

    const fetchAccommodations = useCallback(
        (options?: { skipSingleAccommodation?: boolean; isOneAlgorithmMode?: boolean }) => {
            const { skipSingleAccommodation = false, isOneAlgorithmMode = false } = options || {};
            if (!locationDetails) {
                return;
            }

            const basePropertyTypeIds = toNumericIds(filters.propertyType);
            const expandedPropertyTypeIds = applyPropertyTypeExpansion(basePropertyTypeIds);
            const amenityIds = toNumericIds(filters.amenities);
            const provisionIds = toNumericIds(filters.provision);
            const roomEquipmentIds = toNumericIds(filters.roomEquipment);

            dispatch(
                fetchMapAccommodations({
                    checkInDate: searchCriteria.checkInDate || undefined,
                    checkOutDate: searchCriteria.checkOutDate || undefined,
                    adultsCount: searchCriteria.guests?.adultsCount,
                    childrenCount: searchCriteria.guests?.childrenCount,
                    childrenAges: searchCriteria.guests?.childrenAges,
                    priceFrom: filters.priceFrom ?? undefined,
                    priceTo: filters.priceTo ?? undefined,
                    locationDetails,
                    propertyTypeIds: expandedPropertyTypeIds,
                    amenityIds,
                    provisionIds,
                    roomEquipmentIds,
                    reservationFeatures: filters.reservationFeatures,
                    rating: filters.rating ? mapRatingToApi(filters.rating) : undefined,
                    flexDates: searchCriteria.flexDates,
                    skipSingleAccommodation,
                    isOneAlgorithmMode,
                }),
            );
        },
        [searchCriteria, filters, locationDetails],
    );

    const setIsMapLoaded = useCallback((isLoaded: boolean) => {
        dispatch(setIsMapLoadedAction({ isMapLoaded: isLoaded }));
    }, []);

    return {
        searchCriteria,
        filters,
        selectedAccommodation,
        isFiltersContentVisible,
        isMobileRes,
        updateSearchCriteria,
        updateSearchFilters,
        setSelectedAccommodation,
        setLocationDetails,
        setIsMapLoaded,
        toggleFiltersVisibility,
        setFiltersVisibility,
        getAsUrlParams,
        mapAccommodations,
        realtimeBookingModelAccommodationsCount,
        locationDetails,
        isLoadingAccommodations,
        isMapLoaded,
        isClustering,
        setIsClustering,
        hasGeoChanged,
        accommodationsError,
        setMapLocationDetails,
        setMapPageTitle,
        fetchAccommodations,
        ...rest,
    };
}
